import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BackHandler, Keyboard, Linking, Platform, Pressable, View, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { interpolate, useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { scheduleOnRN } from 'react-native-worklets';
import { StatusBar } from 'expo-status-bar';
import { useT } from '@transifex/react';
import { Button as DsButton, ThemeScope, useMotion, useSheetSnap, useTheme } from '@vidahealth/vida-ds';

import { useDsEnabled } from '../contexts/DesignSystem';
import { useLandingFooter } from '../contexts/LandingFooter';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnboardingSignupUrl } from '../hooks/useOnboardingSignupUrl';
import { ROUTES } from '../utils/constants';
import { testId } from '../utils/testId';
import { BrandCarousel } from './BrandCarousel/BrandCarousel';

const tid = (element: string) => testId('AuthLayout', element);

const DESKTOP_BREAKPOINT = 768;
// vida-ds Sheet's CONTENT_MAX: past it the form scrolls rather than the sheet growing.
const FIT_MAX = 0.9;
const GRABBER_HEIGHT = 28;
// vida-ds Sheet's release rules: a fast flick or a drag past 40% of the sheet closes it.
const FLICK_VELOCITY = 800;
const DISMISS_FRACTION = 0.4;

// Once the keyboard has opened, the sheet stays full height until the app restarts.
const keyboardSession = { full: false };

export type SheetScreen = {
  // The login screen: the sheet starts down, Get started / Log in show, and closing it lowers it.
  entry: boolean;
  onDismiss: React.MutableRefObject<(() => void) | undefined>;
  contentHeight: number;
};

type SheetHostValue = {
  activate: (screen: SheetScreen) => void;
  setContentHeight: (height: number) => void;
};

const SheetHostContext = createContext<SheetHostValue | null>(null);

export const useSheetHost = () => useContext(SheetHostContext);

/**
 * On native phones under vida-ds, the login screens share one carousel and one sheet, and the
 * navigator lives inside the sheet, so moving between screens slides their content across it.
 */
export const SheetHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dsEnabled = useDsEnabled();
  const { width } = useWindowDimensions();
  // Decided at mount: swapping the wrapper when the width crosses 768 would remount the host's navigator.
  const [phone] = useState(() => Platform.OS !== 'web' && width < DESKTOP_BREAKPOINT);
  const active = dsEnabled && phone;
  return active ? <PhoneSheetHost>{children}</PhoneSheetHost> : <>{children}</>;
};

const PhoneSheetHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const t = useT();
  const router = useLoginRouter();
  const signupUrl = useOnboardingSignupUrl();
  const landingFooter = useLandingFooter();
  const { colors } = useTheme();
  const { spring } = useMotion();
  const { height: viewport } = useWindowDimensions();
  const insets = useContext(SafeAreaInsetsContext);
  const topInset = insets?.top ?? 0;

  const screen = useRef<SheetScreen | null>(null);
  const [entry, setEntry] = useState(false);
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(keyboardSession.full);
  const [contentHeight, setContentHeight] = useState(0);

  // The surface is laid out at its tallest and slid down to show only what the screen needs, so
  // screens never resize mid-transition; animating a layout height clipped or dipped them.
  const maxHeight = full ? viewport - topInset : FIT_MAX * viewport;
  const sheetHeight = full
    ? maxHeight
    : Math.min(maxHeight, GRABBER_HEIGHT + (contentHeight || 0.5 * viewport));
  const hidden = useSharedValue(maxHeight - sheetHeight);
  const visible = useSharedValue(sheetHeight);
  const { offset, settleTo, dragTo } = useSheetSnap(viewport);
  // Both hooks hand back fresh functions each render; effects keyed on them would re-run every render.
  const motion = useRef({ settleTo, sheetSpring: spring('sheet') });
  motion.current = { settleTo, sheetSpring: spring('sheet') };

  useEffect(() => {
    visible.value = sheetHeight;
    hidden.value = withSpring(maxHeight - sheetHeight, motion.current.sheetSpring);
  }, [maxHeight, sheetHeight, hidden, visible]);

  useEffect(() => {
    motion.current.settleTo(open ? 0 : viewport);
  }, [open, viewport]);

  const activate = useCallback((next: SheetScreen) => {
    screen.current = next;
    setEntry(next.entry);
    setContentHeight(next.contentHeight);
    if (!next.entry) setOpen(true);
  }, []);

  const hostValue = useMemo(() => ({ activate, setContentHeight }), [activate]);

  const dismiss = useCallback(() => {
    Keyboard.dismiss();
    const current = screen.current;
    if (!current || current.entry) {
      setOpen(false);
      return;
    }
    if (current.onDismiss.current) current.onDismiss.current();
    else if (router.canGoBack()) router.back();
    else router.replace(ROUTES.LOGIN);
    motion.current.settleTo(0);
  }, [router]);

  useEffect(() => {
    if (!open || keyboardSession.full) return undefined;
    const shown = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const subscription = Keyboard.addListener(shown, () => {
      keyboardSession.full = true;
      setFull(true);
    });
    return () => subscription.remove();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      dismiss();
      return true;
    });
    return () => subscription.remove();
  }, [open, dismiss]);

  const drag = Gesture.Pan()
    .onChange((event) => {
      dragTo(Math.max(offset.value + event.changeY, 0));
    })
    .onEnd((event) => {
      if (event.velocityY > FLICK_VELOCITY || offset.value > visible.value * DISMISS_FRACTION) {
        scheduleOnRN(dismiss);
      } else {
        settleTo(0);
      }
    });

  const surfaceStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: offset.value + hidden.value }],
  }));
  const scrimStyle = useAnimatedStyle(() => ({
    opacity: interpolate(offset.value, [0, visible.value], [1, 0], 'clamp'),
  }));

  const entryActions = (
    <View className='gap-[10px]'>
      <DsButton
        hierarchy='secondary'
        size='large'
        fullWidth
        className='bg-raised'
        onPress={() => void Linking.openURL(signupUrl)}
        testID={tid('get-started-button')}
      >
        {t('Get started')}
      </DsButton>
      <DsButton
        hierarchy='glass-inverse'
        size='large'
        fullWidth
        onPress={() => setOpen(true)}
        testID={tid('log-in-button')}
      >
        {t('Log in')}
      </DsButton>
      {landingFooter != null && <View className='mt-ds-8'>{landingFooter}</View>}
    </View>
  );

  return (
    <SheetHostContext.Provider value={hostValue}>
      <View className='flex-1 bg-color-tidal'>
        <StatusBar style='light' />
        {/* The photo is dark in either mode, so what sits on it keeps its light-mode look. */}
        {/* Screen readers get the carousel or the sheet, never both: what's out of view stays out of reach. */}
        <View
          className='flex-1'
          accessibilityElementsHidden={open}
          importantForAccessibility={open ? 'no-hide-descendants' : 'auto'}
        >
          <ThemeScope mode='light' fill className='flex-1'>
            <BrandCarousel variant='fullscreen' footer={entry ? entryActions : undefined} behindSheet={open} />
          </ThemeScope>
        </View>
        <Animated.View
          pointerEvents={open ? 'auto' : 'none'}
          accessibilityElementsHidden={!open}
          importantForAccessibility={open ? 'auto' : 'no-hide-descendants'}
          style={[{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }, scrimStyle]}
        >
          <Pressable
            accessibilityRole='button'
            accessibilityLabel={t('Close')}
            onPress={dismiss}
            style={{ flex: 1, backgroundColor: colors['scrim-overlay'] }}
            testID={tid('sheet-scrim')}
          />
        </Animated.View>
        {/* The navigator stays mounted while the sheet is down, so the login screen keeps running. */}
        <Animated.View
          accessibilityElementsHidden={!open}
          importantForAccessibility={open ? 'auto' : 'no-hide-descendants'}
          style={[{ position: 'absolute', right: 0, bottom: 0, left: 0, height: maxHeight }, surfaceStyle]}
          testID={tid('sheet')}
        >
          <View className='flex-1 overflow-hidden rounded-t-button-base bg-canvas'>
            <GestureDetector gesture={drag}>
              <View className='items-center py-ds-12' style={{ height: GRABBER_HEIGHT }}>
                <View className='h-ds-4 w-ds-48 rounded-medium bg-tone-faint' />
              </View>
            </GestureDetector>
            <View className='flex-1'>{children}</View>
          </View>
        </Animated.View>
      </View>
    </SheetHostContext.Provider>
  );
};
